import { Injectable } from '@angular/core';
import { quizStartFromTextSchema } from '@shared/ragai.schemas';
import confetti from 'canvas-confetti';
import { z } from 'zod';
import { BUTTONCARDBUTTON } from './shared/components/ai-bot/utils/bot-chat-dialog/message-card/button-card/button-card.component';
import { OpenaiService } from './shared/services/ai/text/openai.service';
import { CardsManagerService } from './shared/services/cards-manager.service';
import { DOMManagerService } from './shared/services/dommanager.service';
import { DownloadManagerService } from './shared/services/download-manager.service';
import { EventManagerService } from './shared/services/event-manager/event-manager.service';
import { MessageManagerService } from './shared/services/message-manager.service';
import { OverlayManagerService } from './shared/services/overlay-manager.service';
import { QuizHistoryService } from './shared/services/quiz-history.service';
import { ScoreService } from './shared/services/score/score.service';
import { WaitForManagerService } from './shared/services/wait-for-manager.service';
export type QuizQuestion = {
  q: string;
  r: string;
  w: string[];
};
/**
 * Generiert und steuert KI-basierte Quizzes im Chat (`RAGAI.QUIZ`).
 * @remarks
 * Fragen werden per KI aus einem Ausgangstext generiert ({@link startFromText}, nutzt `RAGAI.AITEXT_OPENAI`)
 * und dann nacheinander aus der `questions`-Warteschlange abgefragt ({@link nextQuestion}). `ANSWER_MODE`
 * schaltet zwischen zwei grundverschiedenen Antwort-Mechaniken um: `'buttons'` (Multiple-Choice per Card-Buttons)
 * und `'freetext'` (die nächste normale Prompt-Eingabe des Nutzers wird als Antwort auf `CURRENT_QUESTION` gewertet).
 * Jeder Quiz-Lauf bekommt eine eigene `CURRENT_QUIZ_UNIQUE_ID`, an die alle internen Event-Listener
 * (`RAGAI.QUIZ.*`) gebunden sind; {@link cleanupQuiz} muss diese IDs wieder abmelden, sonst häufen sich
 * Listener über mehrere Quiz-Durchläufe hinweg an.
 * Bei aktivem `QUIZ_USE_HISTORY` wird jede Antwort projektweit protokolliert (`RAGAI.PROJECTKEYVAL` über
 * `QuizHistoryService`) und fließt in zukünftige Frage-Generierungen als Prompt-Kontext ein (über Bots hinweg,
 * `QUIZ_HISTORY_LIMIT` begrenzt wie viele letzte Einträge einfließen).
 */
@Injectable({
  providedIn: 'root'
})
export class QuizManagerService {
  public questions: QuizQuestion[] = [];
  public CURRENT_QUESTIONS: string[] = [];
  private currentRight: string = "";
  private confetti = confetti;
  public CURRENT_QUIZ_UNIQUE_ID: string = "";
  public RIGHT_ANSWERS: number = 0;
  public WRONG_ANSWERS: number = 0;
  public CONF_QUESTIONS_COUNT: number = 5;
  public CONF_WRONG_ANSWER_COUNT: number = 2;
  public ADDITIONAL_PROMPT_INFO: string = "";
  public BUTTON_CLASS: string = "";
  /** 'buttons' (Standard, Multiple-Choice-Buttons) oder 'freetext' (Antwort ueber das Prompt-Eingabefeld) */
  public ANSWER_MODE: 'buttons' | 'freetext' = 'buttons';
  /** Ausgangsdokument/-text des laufenden Quiz, wird im Freitext-Modus zur Antwortbewertung benoetigt */
  public SOURCE_TEXT: string = "";
  /** Aktuell offene Frage im Freitext-Modus, auf die eine Prompt-Eingabe als Antwort gewertet wird */
  public CURRENT_QUESTION: QuizQuestion | null = null;
  /** Ob Frage/Antwort-Verlauf projektweit protokolliert und in den Generierungs-Prompt eingebettet wird */
  public QUIZ_USE_HISTORY: boolean = true;
  /** Anzahl der letzten Verlaufseintraege, die in den Generierungs-Prompt eingebettet werden (<=0 = alle) */
  public QUIZ_HISTORY_LIMIT: number = 20;
  /** Skill-/Dokument-ID der Quiz-Quelle, wird mit in den Verlauf protokolliert (optional) */
  public QUIZ_SOURCE_SKILL_ID: string = "";
  /** Zeitpunkt, zu dem die aktuell offene Frage angezeigt wurde (fuer die Verlaufsprotokollierung) */
  private lastQuestionAskedAt: string = "";

  public get IS_RUNNING(): boolean {
    return this.CURRENT_QUIZ_UNIQUE_ID != "";
  }

  public get QUESTIONS_UPCOMMING(): number {
    return this.questions.length;
  }
  public get ASKED_QUESTIONS(): number {
    return this.CONF_QUESTIONS_COUNT - this.questions.length;
  }

  public get ANSWERED_QUESTIONS(): number {
    return this.RIGHT_ANSWERS + this.WRONG_ANSWERS;
  }
  public get CURRENT_QUESTION_NUMBER(): number {
    return this.ASKED_QUESTIONS + 1;
  }



  public LOADING_MSG: string = $localize`:@@quizLoadingMsg:Quiz wird vorbereitet...`;
  public ANSWER_EVAL_ERROR_MSG: string = $localize`:@@quizAnswerEvalErrorMsg:Deine Antwort konnte nicht ausgewertet werden. Bitte versuche es erneut.`;
  constructor(
    private DOM: DOMManagerService,
    private OVERLAY: OverlayManagerService,
    private DOWNLOAD: DownloadManagerService,
    private MSG: MessageManagerService,
    private AITEXT_OPENAI: OpenaiService,
    private WAITFOR: WaitForManagerService,
    private EVENT: EventManagerService,
    private CARDS: CardsManagerService,
    private QUIZ_HISTORY: QuizHistoryService,
    private SCORE: ScoreService) {

  }

  /** Gibt den aktuellen Quiz-Zustand als Debug-Karte im Chatverlauf aus. */
  public async debug(eventData: unknown = {}) {
    let debugStr = "RAGAI.QUIZ.\n";
    ["RIGHT_ANSWERS", "WRONG_ANSWERS", "CURRENT_QUIZ_UNIQUE_ID", "CONF_QUESTIONS_COUNT", "CONF_WRONG_ANSWER_COUNT", "QUESTIONS_COUNT", "QUESTIONS_UPCOMMING", "ASKED_QUESTIONS", "ANSWERED_QUESTIONS", "CURRENT_QUESTION_NUMBER"].forEach((prop) => {
      // @ts-expect-error
      debugStr += `\t -> ${prop}: ${String(this[prop])}\n`;
    });

    if (eventData && Object.keys(eventData).length > 0) {
      debugStr += "\n<b>CURRENT:</b>\n";
      debugStr += JSON.stringify(eventData, null, 2);

    }
    await this.CARDS.debugEvent("<div class='alert alert-info'><b>QUIZ INFO</b><pre>" + debugStr + "</pre></div>");
    await this.WAITFOR.seconds(0.5);
  }

  /** Löst das `RAGAI.QUIZ.END`-Event aus, um das laufende Quiz vorzeitig zu beenden. */
  public end() {
    this.EVENT.trigger("RAGAI.QUIZ.END", {});
  }
  /** Kurzalias für end(). */
  public endQuiz() {
    this.EVENT.trigger("RAGAI.QUIZ.END", {});
  }


  /** Setzt den kompletten Quiz-Zustand zurück und meldet alle an die aktuelle Quiz-ID gebundenen Event-Listener ab. */
  public cleanupQuiz() {
    const uuid = this.CURRENT_QUIZ_UNIQUE_ID;
    this.CURRENT_QUIZ_UNIQUE_ID = "";
    this.ADDITIONAL_PROMPT_INFO = "";
    this.questions = [];
    this.CURRENT_QUESTIONS = [];
    this.RIGHT_ANSWERS = 0;
    this.WRONG_ANSWERS = 0;
    this.ANSWER_MODE = 'buttons';
    this.SOURCE_TEXT = "";
    this.CURRENT_QUESTION = null;
    this.EVENT.off(uuid + "-start");
    this.EVENT.off(uuid + "-right");
    this.EVENT.off(uuid + "-wrong");
    this.EVENT.off(uuid + "-end");
    this.EVENT.off(uuid + "-history-right");
    this.EVENT.off(uuid + "-history-wrong");
    this.EVENT.off("RAGAI_QUIZ_INIT");

  }

  /** Startet ein neues Quiz: generiert Fragen per KI aus dem Ausgangstext und triggert `RAGAI.QUIZ.START`. */
  public async startFromText(source: string | z.input<typeof quizStartFromTextSchema>): Promise<void> {
    const parsed = quizStartFromTextSchema.safeParse(source);
    if (parsed.success && typeof source === 'object') {
      source = parsed.data.source;
    }
    this.SOURCE_TEXT = source as string;
    this.CURRENT_QUIZ_UNIQUE_ID = 'quiz-' + Date.now() + '-' + Math.floor(Math.random() * 1000);
    const uuid = this.CURRENT_QUIZ_UNIQUE_ID;
    this.EVENT.on("RAGAI.QUIZ.RIGHTANSWER", (_name, eventData) => { void this.logHistoryEntry(eventData); }, uuid + "-history-right");
    this.EVENT.on("RAGAI.QUIZ.WRONGANSWER", (_name, eventData) => { void this.logHistoryEntry(eventData); }, uuid + "-history-wrong");
    this.EVENT.trigger("RAGAI.QUIZ.INIT", this.CURRENT_QUIZ_UNIQUE_ID);
    this.OVERLAY.loading(this.LOADING_MSG);
    this.questions = JSON.parse(await this.getQuestionsFromText(source as string)) as QuizQuestion[];
    this.CURRENT_QUESTIONS = this.questions.map((q) => q.q);
    this.OVERLAY.close();
    this.EVENT.trigger("RAGAI.QUIZ.START", {});

  }

  /** Stellt die nächste Frage aus der Warteschlange (Button- oder Freitext-Modus) oder beendet das Quiz, wenn keine mehr übrig sind. */
  public async nextQuestion() {
    if (this.questions.length == 0) {
      // DER TEST IST VORBEI
      this.EVENT.trigger("RAGAI.QUIZ.END", {});
    } else {
      const curr: QuizQuestion | undefined = this.questions.shift();
      if (curr) {
        await this.MSG.botMessage(curr.q);
        this.lastQuestionAskedAt = new Date().toISOString();
        if (this.ANSWER_MODE === 'freetext') {
          this.CURRENT_QUESTION = curr;
          // Sicherstellen, dass das Prompt-Eingabefeld sichtbar ist, unabhaengig davon,
          // ob eine "Bei Start"-Aktion (z.B. Footer ausblenden) im Blockly-Block konfiguriert wurde.
          this.DOM.showFooter();
        } else {
          await this.showAnswerOptions(curr);
        }
      }
    }
  }

  /**
   * Protokolliert eine beantwortete Frage (Button- oder Freitext-Modus, ausgeloest ueber die
   * RAGAI.QUIZ.RIGHTANSWER/WRONGANSWER Events) projektweit fuer den aktuellen Nutzer.
   */
  private async logHistoryEntry(eventData: unknown): Promise<void> {
    if (!this.QUIZ_USE_HISTORY) return;
    const data = eventData as { question: string, answered: string, solution: string, right: boolean };
    await this.QUIZ_HISTORY.logAnswer({
      question: data.question,
      correctAnswer: data.solution,
      givenAnswer: data.answered,
      correct: data.right,
      askedAt: this.lastQuestionAskedAt,
      answeredAt: new Date().toISOString(),
      skillId: this.QUIZ_SOURCE_SKILL_ID
    });
  }

  /** Loescht den projektweiten Quiz-Verlauf des aktuellen Nutzers (fuer Blockly nutzbar). */
  public async clearHistory(): Promise<void> {
    await this.QUIZ_HISTORY.clearHistory();
  }

  /** Generiert per KI ein JSON-Array von Quiz-Fragen (inkl. richtiger/falscher Antworten) aus einem Ausgangstext. */
  public async getQuestionsFromText(t: string): Promise<string> {

    let historyBlock = "";
    if (this.QUIZ_USE_HISTORY) {
      const historyText = await this.QUIZ_HISTORY.getHistoryAsPromptText(this.QUIZ_HISTORY_LIMIT);
      if (historyText) {
        historyBlock = `\n      Bisherige Quiz-Historie dieses Nutzers (bereits gestellte Fragen mit gegebener Antwort\n      und ob sie richtig beantwortet wurde):\n      ${historyText}\n`;
      }
    }

    const p: string = `hier hast du einen text
      ###
      ${t}
      ###
      nehme den text und extrahiere ${this.CONF_QUESTIONS_COUNT.toString()} fragen
      gebe auch die antwortmöglichkkeiten 1 richtige ${this.CONF_WRONG_ANSWER_COUNT.toString()} falsche
      eine antwort soll richtig sein.

      ${historyBlock}


      ${this.ADDITIONAL_PROMPT_INFO}

      antworte in dem format.


      [
      ${Array.from({ length: this.CONF_QUESTIONS_COUNT }, (_, i) =>
      `  {"q":"Frage ${(i + 1).toString()}", "r":"richtige antwort", "w":[${Array.from({ length: this.CONF_WRONG_ANSWER_COUNT }, (_, j) => `"falsch${(j + 1).toString()}"`).join(",")}]}`)
        .join(",\n")}
      ]
      `;



    let r = await this.AITEXT_OPENAI.chatCompletion(p, 'gpt-4o-mini', 0.7, false) as string;
    r = r.slice(r.indexOf('['), r.lastIndexOf(']') + 1);
    return r;
  }


  /** Zeigt die aktuelle Frage mit gemischten Antwort-Buttons (richtig + falsch) im Chatverlauf an. */
  public async showAnswerOptions(curr: QuizQuestion) {
    const buttons: BUTTONCARDBUTTON[] = [];
    this.currentRight = curr.r;
    for (let i = curr.w.length - 1; i >= 0; i--) {
      const eventData = {
        question: curr.q,
        answered: curr.w[i],
        solution: curr.r,
        right: false
      };
      const newBtn: BUTTONCARDBUTTON = {
        text: curr.w[i] + "",
        className: this.BUTTON_CLASS,
        onClick: `
        function(){
        RAGAI.QUIZ.WRONG_ANSWERS +=1 ;
        RAGAI.EVENTS.trigger("RAGAI.QUIZ.WRONGANSWER", ${JSON.stringify(eventData)});
        }`

      };

      buttons.push(newBtn);

    }

    const eventDataRight = {
      question: curr.q,
      answered: curr.r,
      solution: curr.r,
      right: true
    };

    const rBtn: BUTTONCARDBUTTON = {
      text: curr.r,
      className: this.BUTTON_CLASS,
      onClick: `function(){
      RAGAI.QUIZ.RIGHT_ANSWERS +=1 ;
      RAGAI.EVENTS.trigger("RAGAI.QUIZ.RIGHTANSWER", ${JSON.stringify(eventDataRight)});}`

    };
    buttons.push(rBtn);

    for (let i = buttons.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1)); // zufälliger Index von 0 bis i
      // @ts-expect-error
      [buttons[i], buttons[j]] = [buttons[j], buttons[i]]; // Elemente tauschen
    }
    await this.CARDS.buttons(buttons, "quiz-buttons");
  }

  /**
   * Bewertet die Freitext-Antwort des Nutzers anhand des Ausgangsdokuments und der
   * extrahierten Referenz-Antwort mittels einer strukturierten KI-Antwort (structuredResponse).
   * Gibt bei einem Fehler (z.B. Netzwerkproblem) `null` zurueck.
   */
  public async evaluateFreetextAnswer(curr: QuizQuestion, userAnswer: string): Promise<{ correct: boolean, reasoning: string } | null> {
    const promptInput = `Kontext:
      ###
      ${this.SOURCE_TEXT}
      ###

      Frage: ${curr.q}
      Richtige Antwort: ${curr.r}
      Antwort: ${userAnswer}

      Reagiere direkt auf die gegebene Antwort. Teile dem Nutzer mit, ob sie richtig oder falsch ist, und begruende dies kurz und fachlich.
      ${this.ADDITIONAL_PROMPT_INFO}`;

    try {
      return await this.AITEXT_OPENAI.structuredResponse<{ correct: boolean, reasoning: string }>({
        promptInput,
        jsonSchema: {
          name: "quiz_answer_evaluation",
          schema: {
            type: "object",
            properties: {
              correct: { type: "boolean" },
              reasoning: { type: "string" }
            },
            required: ["correct", "reasoning"],
            additionalProperties: false
          }
        },
        model: 'gpt-4o-mini',
        temperature: 0.2
      });
    } catch {
      return null;
    }
  }

  /**
   * Wird als RAGAI.BEFORE_SEND-Hook registriert (siehe QUIZ_START_FROM_TEXT_FREETEXT Blockly-Block).
   * Faengt die normale Prompt-Eingabe des Nutzers ab, solange eine Freitext-Quizfrage offen ist,
   * wertet sie aus und unterbindet dadurch die normale KI-Chat-Anfrage (Rueckgabe `null`).
   * Ist kein Freitext-Quiz aktiv bzw. keine Frage offen, wird die Nachricht unveraendert
   * durchgereicht, damit der normale Chat-Flow unberuehrt bleibt.
   */
  public async handleFreetextAnswer(message: string): Promise<string | null> {
    if (!this.IS_RUNNING || this.ANSWER_MODE !== 'freetext' || this.CURRENT_QUESTION == null) {
      return message;
    }
    const curr = this.CURRENT_QUESTION;
    this.CURRENT_QUESTION = null;

    await this.MSG.userMessageDirect(message);

    const evaluation = await this.evaluateFreetextAnswer(curr, message);
    if (evaluation == null) {
      await this.MSG.botMessage(this.ANSWER_EVAL_ERROR_MSG);
      this.CURRENT_QUESTION = curr;
      return null;
    }

    await this.MSG.botMessage(evaluation.reasoning);

    const eventData = {
      question: curr.q,
      answered: message,
      solution: curr.r,
      right: evaluation.correct,
      reasoning: evaluation.reasoning
    };

    if (evaluation.correct) {
      this.RIGHT_ANSWERS += 1;
      this.EVENT.trigger("RAGAI.QUIZ.RIGHTANSWER", eventData);
    } else {
      this.WRONG_ANSWERS += 1;
      this.EVENT.trigger("RAGAI.QUIZ.WRONGANSWER", eventData);
    }

    return null;
  }
}
